import { useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { QueueAction } from '@rossum/api-client/schemas';
import { IconCustomFolderMove } from '@rossum/ui/icons/tabler';
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Skeleton,
  Stack,
} from '@rossum/ui/material';
import { getUnsafe } from '../../../lib/helpers';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { QueueSelect } from '../../queues/select-queue/QueueSelect';
import { createForm } from './createForm';
import { createHeader } from './createHeader';

const statusKeepingEvents: ReadonlyArray<QueueAction['event']> = [
  'annotation_import_succeeded',
  'annotation_import_failed',
  'annotation_export_succeeded',
  'annotation_export_failed',
];

const getReimportHelperTextId = (
  reimport: boolean,
  event: QueueAction['event']
) => {
  if (reimport) {
    return 'features.queueSettings.rules.actions.moveDocument.reimport.helperText.reimport';
  }

  return statusKeepingEvents.includes(event)
    ? 'features.queueSettings.rules.actions.moveDocument.reimport.helperText.keepStatus'
    : 'features.queueSettings.rules.actions.moveDocument.reimport.helperText.toReview';
};

const moveDocumentActionPayloadSchema = (intl?: IntlShape) =>
  z.object({
    queue: z
      .string()
      .nullable()
      .default(null)
      .refine(
        val => val !== null,
        intl?.formatMessage({
          id: 'features.queueSettings.rules.actions.moveDocument.payload.queueId.required',
        })
      ),
    reimport: z.boolean().default(false),
  });

const MoveDocumentActionForm = createForm(
  ({ value, onChange, errors, event }) => {
    const { isLoading, queues } = useWorkspacesWithQueues({
      enableQueries: true,
    });

    const stableValue = useMemo(
      () => queues?.find(queue => value.queue === queue.url) ?? null,
      [queues, value]
    );

    const intl = useIntl();

    return isLoading ? (
      <Skeleton height={40} />
    ) : (
      <>
        <QueueSelect
          value={stableValue?.url ?? null}
          onChange={queue =>
            onChange({
              ...value,
              // @ts-expect-error queue_id is nullable, just the refine narrows the type to non-nullable
              queue,
            })
          }
          error={getUnsafe(errors, 'queue') !== undefined}
          helperText={getUnsafe(errors, 'queue.message')?.toString()}
        />
        <Stack>
          <FormControlLabel
            sx={{ pl: 1 }}
            control={
              <Checkbox
                checked={value.reimport === true}
                onClick={() =>
                  onChange({ ...value, reimport: !value.reimport })
                }
                size="small"
                edge="start"
              />
            }
            label={intl.formatMessage({
              id: 'features.queueSettings.rules.actions.moveDocument.reimport.label',
            })}
          />
          <FormHelperText sx={{ mt: 0, mx: 1 }}>
            {intl.formatMessage({
              id: getReimportHelperTextId(value.reimport, event),
            })}
          </FormHelperText>
        </Stack>
      </>
    );
  },
  (intl?: IntlShape) => moveDocumentActionPayloadSchema(intl)
);

const MoveDocumentActionHeader = createHeader(
  ({ value, RenderComponent }) => {
    const intl = useIntl();
    const { isLoading, queues } = useWorkspacesWithQueues({
      enableQueries: true,
    });

    const stableValue = useMemo(
      () => queues?.find(queue => value.queue === queue.url) ?? null,
      [queues, value]
    );
    return (
      <RenderComponent
        entries={[
          {
            key: 'queue',
            label: intl.formatMessage({
              id: 'features.queueSettings.rules.actions.moveDocument.targetQueue.label',
            }),
            value: isLoading ? '...' : `${stableValue?.name ?? ''}`,
            sx: {
              maxWidth: 600,
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            },
          },
        ]}
      />
    );
  },
  (intl?: IntlShape) => moveDocumentActionPayloadSchema(intl)
);

export const moveDocumentAction = {
  icon: IconCustomFolderMove,
  resolveAction: (a: QueueAction) => a.type === 'change_queue',
  serialized: { type: 'change_queue' as const },
  intlKey: 'moveDocument' as const,
  groupIntlKey: 'automation' as const,
  form: MoveDocumentActionForm,
  header: MoveDocumentActionHeader,
  supportedEvents: [
    'annotation_imported',
    'annotation_import_succeeded',
    'annotation_import_failed',
    'annotation_confirmed',
    'annotation_exported',
    'annotation_export_succeeded',
    'annotation_export_failed',
  ] as const,
  payloadSchema: (intl?: IntlShape) => moveDocumentActionPayloadSchema(intl),
};
